// Layer 3 (Transformations): pure route predicates + the project-scoped
// module nav. Fork-owned OptiFlow function rail (function-rail-restructure
// F0). Home, AI Assistant, and Settings stay chrome rows.

import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  BookOpen,
  CircleDollarSign,
  ClipboardCheck,
  Factory,
  FileText,
  FlaskConical,
  Home,
  Gauge,
  Scale,
  ShieldAlert,
  Target,
  LayoutDashboard,
  MessageCircle,
  Plug,
  Settings,
  Sparkles,
  Users,
  Workflow,
  Wrench,
} from 'lucide-react';
import type { Persona } from '@/shared/types';
import type { AdvisoryRailBadge } from '@/versions/v1/shared/advisory-nav.transform';

export type ProjectSidebarNavItem = {
  segment:
    | ''
    | 'chat'
    | 'overview'
    | 'plant-model'
    | 'capacity'
    | 'ingestion'
    | 'process'
    | 'quality'
    | 'metal-accounting'
    | 'planning'
    | 'cost'
    | 'advisory'
    | 'compliance'
    | 'risk'
    | 'maintenance'
    | 'downtime'
    | 'meeting'
    | 'documents'
    | 'reports'
    | 'workflows'
    | 'settings';
  label: string;
  Icon: LucideIcon;
  blurb?: string;
  badgeCount?: number;
  /** The badge's accessible name and tooltip — what the count counts (#732). */
  badgeLabel?: string;
};

export const PROJECT_SIDEBAR_NAV_ITEMS: readonly ProjectSidebarNavItem[] = [
  { segment: '', label: 'Home', Icon: Home },
  { segment: 'chat', label: 'AI Assistant', Icon: MessageCircle },
  {
    segment: 'overview',
    label: 'Overview',
    Icon: LayoutDashboard,
    blurb: 'Persona-resolved role landing',
  },
  {
    segment: 'plant-model',
    label: 'Plant model',
    Icon: Factory,
    blurb: 'Bottleneck and spec-conformance',
  },
  {
    segment: 'capacity',
    label: 'Capacity & strategy',
    Icon: Gauge,
    blurb: 'Grade-adjusted capacity and value uplift',
  },
  {
    segment: 'ingestion',
    label: 'Ingestion',
    Icon: Plug,
    blurb: 'Reconstruct the plant from artefacts',
  },
  {
    segment: 'process',
    label: 'Process',
    Icon: Activity,
    blurb: 'Loop status and readings',
  },
  {
    segment: 'quality',
    label: 'Quality & Lab',
    Icon: FlaskConical,
    blurb: 'Sample register, impurities, due samples',
  },
  {
    segment: 'metal-accounting',
    label: 'Metal accounting',
    Icon: Scale,
    blurb: 'Reconciliation and metal balance',
  },
  {
    segment: 'advisory',
    label: 'Advisory',
    Icon: Sparkles,
    blurb: 'Case queue, approvals, and alarms',
    // #732 — no static badgeCount: the figure is the flow read's own open-case
    // count at the cursor, grafted on by `visibleProjectNav` (`advisoryRailBadge`).
  },
  {
    segment: 'compliance',
    label: 'Compliance',
    Icon: ClipboardCheck,
    blurb: 'HSE dashboard, permits, and regulatory compliance',
  },
  {
    segment: 'risk',
    label: 'Risk',
    Icon: ShieldAlert,
    blurb: 'Risk register, interlocks, and control checks',
  },
  {
    segment: 'maintenance',
    label: 'Maintenance',
    Icon: Wrench,
    blurb: 'Work orders, strategy, and downtime',
  },
  {
    segment: 'downtime',
    label: 'Downtime',
    Icon: Activity,
    blurb: 'Pareto causes, reliability, and shift correlation',
  },
  {
    segment: 'meeting',
    label: 'Daily meeting',
    Icon: Users,
    blurb: '3W board, shift handover, and CCC review',
  },
  {
    segment: 'documents',
    label: 'Documents',
    Icon: BookOpen,
    blurb: 'Procedures and training material',
  },
  {
    segment: 'reports',
    label: 'Reports',
    Icon: FileText,
    blurb: 'Weekly and template reports',
  },
  {
    segment: 'workflows',
    label: 'Workflows',
    Icon: Workflow,
    blurb: 'Agent workflows and their runs',
  },
  {
    segment: 'planning',
    label: 'Planning',
    Icon: Target,
    blurb: 'Budget, stretch, and latest estimate',
  },
  {
    segment: 'cost',
    label: 'Cost & revenue',
    Icon: CircleDollarSign,
    blurb: 'MTD cost, revenue, and improvements',
  },
  { segment: 'settings', label: 'Settings', Icon: Settings },
] as const;

const CHROME_SEGMENTS = new Set<ProjectSidebarNavItem['segment']>(['', 'chat', 'settings']);

type ProjectProductSegment = Exclude<
  ProjectSidebarNavItem['segment'],
  '' | 'chat' | 'settings'
>;

// #195 (specvactual.B3) — rail visibility is two orthogonal predicates:
// phase (what ships in demo 1) and role (what this persona reaches for first).
// Off-rail modules keep their routes; chrome rows are never filtered.
//
// Phase 1 = Modules 1 + 3 (+ 14 as an engine) — Paul 2026-08-26. The rail is
// Overview (the hub) + M1 (plant-model · ingestion · quality) + M3 (advisory ·
// process). Everything else is off the rail for every persona (Miles
// 2026-08-27): M2 metal accounting, M4 reports, M5 planning, M6 risk, M7
// compliance, M9 maintenance, M12/13 cost. Shipped surfaces stay routable.
//
// paul-tarp.A20 (#422, Miles 2026-09-15) — M5–M13 return as screenshot-grade
// surfaces under *Planning*, *Operations* (risk · maintenance · daily meeting ·
// documents), *Cost* and, after glass-run.A7 (#494), the existing Compliance
// group. The 2026-09-23 synthetic-data-first ruling clears the prior M7 park.
export const PHASE_OFF_SEGMENTS: ReadonlySet<ProjectProductSegment> = new Set([]);

const PHASE_ON_PRODUCT_SEGMENTS: readonly ProjectProductSegment[] =
  PROJECT_SIDEBAR_NAV_ITEMS.map((item) => item.segment).filter(
    (segment): segment is ProjectProductSegment => !CHROME_SEGMENTS.has(segment),
  ).filter((segment) => !PHASE_OFF_SEGMENTS.has(segment));

// route-reads paul-feedback.A8 slice 1 (#268) — Reports rejoins the rail
// under *Role reporting* (paired with Overview) for the five personas the
// decision record (#268 §2-4) and the standing rule (#262) actually name or
// implicate: GM and Superintendent read it for oversight, Metallurgist
// authors most of its recommendations, and Maintenance / Control room are
// the two named deciders besides the metallurgist (paul-feedback.A8 slice
// 4). Ops supervisor and Operator stay off it, consistent with their
// existing absence from every other management-reporting segment
// (`planning`, `cost`) — floor-shift roles, not report consumers.
//
// komatsu.K19 (#693) — Workflows sits directly after Reports under *Role
// reporting* for every persona that sees Advisory: the agent layer's
// catalogue and runs are read by whoever reads the cases they produce.
export const RAIL_SEGMENTS_BY_PERSONA: Record<
  Persona,
  readonly ProjectProductSegment[]
> = {
  // Role axis within the phase-1 set. Greg: the GM never sees the plant model.
  // Planning (M5) — GM and Superintendent are its primary users (docs/logic/planning/overview.md).
  GM: ['overview', 'capacity', 'metal-accounting', 'advisory', 'reports', 'workflows', 'meeting', 'risk', 'planning', 'cost', 'compliance'],
  Superintendent: ['overview', 'capacity', 'metal-accounting', 'advisory', 'process', 'quality', 'reports', 'workflows', 'meeting', 'risk', 'maintenance', 'downtime', 'documents', 'planning', 'compliance'],
  Metallurgist: ['overview', 'plant-model', 'capacity', 'metal-accounting', 'ingestion', 'process', 'quality', 'advisory', 'reports', 'workflows', 'meeting', 'documents'],
  'Senior metallurgist': ['overview', 'plant-model', 'capacity', 'metal-accounting', 'ingestion', 'process', 'quality', 'advisory', 'reports', 'workflows', 'meeting', 'risk', 'documents'],
  'Ops supervisor': ['overview', 'process', 'quality', 'advisory', 'workflows', 'meeting', 'documents'],
  'Control room': ['overview', 'plant-model', 'process', 'quality', 'advisory', 'reports', 'workflows', 'meeting', 'documents'],
  Operator: ['overview', 'plant-model', 'process', 'quality', 'advisory', 'workflows', 'documents'],
  Maintenance: ['overview', 'advisory', 'process', 'reports', 'workflows', 'meeting', 'risk', 'maintenance', 'downtime', 'documents'],
  Admin: PHASE_ON_PRODUCT_SEGMENTS,
  Editor: PHASE_ON_PRODUCT_SEGMENTS,
  Reviewer: PHASE_ON_PRODUCT_SEGMENTS,
  Viewer: PHASE_ON_PRODUCT_SEGMENTS,
};

function personaProductSegments(persona: Persona): ReadonlySet<ProjectProductSegment> {
  return new Set(
    RAIL_SEGMENTS_BY_PERSONA[persona].filter(
      (segment) => !PHASE_OFF_SEGMENTS.has(segment),
    ),
  );
}

/** Persona- and phase-scoped project rail — chrome rows always included.
 *  Item order follows group order (reach-for-first within what the role sees).
 *  `advisoryBadge` (#732) grafts the Advisory row's flow-read count on; the
 *  static items never carry it, so the rail cannot render a stale seed figure. */
export function visibleProjectNav(
  persona: Persona,
  advisoryBadge?: AdvisoryRailBadge | null,
): {
  items: readonly ProjectSidebarNavItem[];
  groups: readonly ProjectNavGroup[];
} {
  const visibleProduct = personaProductSegments(persona);
  const groups = projectNavGroupsComplete()
    .map((group) => ({
      ...group,
      segments: group.segments.filter((segment) => {
        if (CHROME_SEGMENTS.has(segment)) return true;
        return visibleProduct.has(segment as ProjectProductSegment);
      }),
    }))
    .filter((group) => group.segments.length > 0);
  const orderedSegments = groups.flatMap((group) => group.segments);
  const items = orderedSegments
    .map((segment) => PROJECT_SIDEBAR_NAV_ITEMS.find((item) => item.segment === segment))
    .filter((item): item is ProjectSidebarNavItem => Boolean(item))
    .map((item) =>
      item.segment === 'advisory' && advisoryBadge
        ? { ...item, badgeCount: advisoryBadge.count, badgeLabel: advisoryBadge.label }
        : item,
    );
  return { items, groups };
}

/** Product modules on the function rail — used on project home as well. */
export const PROJECT_PRODUCT_NAV_ITEMS = PROJECT_SIDEBAR_NAV_ITEMS.filter(
  (item) => !CHROME_SEGMENTS.has(item.segment),
);

/** Group captions on the project rail — product region names only. */
export type ProjectNavGroup = {
  caption: string | null;
  segments: readonly ProjectSidebarNavItem['segment'][];
};

export const PROJECT_NAV_GROUPS: readonly ProjectNavGroup[] = [
  { caption: null, segments: ['', 'chat'] },
  { caption: 'Plant', segments: ['plant-model', 'capacity', 'ingestion', 'quality'] },
  { caption: 'Advisory', segments: ['advisory', 'process'] },
  { caption: 'Role reporting', segments: ['overview', 'reports', 'workflows'] },
  { caption: 'Metal accounting', segments: ['metal-accounting'] },
  { caption: 'Operations', segments: ['meeting', 'risk', 'maintenance', 'downtime', 'documents'] },
  { caption: 'Planning', segments: ['planning'] },
  { caption: 'Cost', segments: ['cost'] },
  { caption: 'Compliance', segments: ['compliance'] },
  { caption: null, segments: ['settings'] },
];

/** Groups plus any nav item no group claims, appended ungrouped — a new rail
 *  item must never silently vanish because PROJECT_NAV_GROUPS wasn't updated. */
export function projectNavGroupsComplete(): readonly ProjectNavGroup[] {
  const covered = new Set(PROJECT_NAV_GROUPS.flatMap((group) => group.segments));
  const leftover = PROJECT_SIDEBAR_NAV_ITEMS.filter(
    (item) => !covered.has(item.segment),
  ).map((item) => item.segment);
  return leftover.length === 0
    ? PROJECT_NAV_GROUPS
    : [...PROJECT_NAV_GROUPS, { caption: null, segments: leftover }];
}

const PROJECT_ROUTE_SLUG_RE = /^\/projects\/([^/]+)/;
const PROJECT_CHAT_ROUTE_RE = /^\/projects\/([^/]+)\/chat(?:\/|$)/;
const PROJECT_SETTINGS_ROUTE_RE = /^\/settings\/projects\/([^/]+)/;

/** True on `/projects` exactly — the portfolio index. */
export function isPortfolioPath(pathname: string): boolean {
  return pathname === '/projects';
}

/** True on `/projects/<slug>/chat` — the project-scoped AI Assistant. */
export function isProjectChatPath(pathname: string): boolean {
  return PROJECT_CHAT_ROUTE_RE.test(pathname);
}

/** True on chat-home surfaces that mount Pinned/Recents in the sidebar (`/` and project chat). */
export function isChatThreadListPath(pathname: string): boolean {
  if (pathname === '/') return true;
  return isProjectChatPath(pathname);
}

/** True for `/settings/projects/[id]` — project settings, not personal settings. */
export function isProjectSettingsPath(pathname: string): boolean {
  return PROJECT_SETTINGS_ROUTE_RE.test(pathname);
}

/** True when the main panel is a full-width chat surface (chat-first framing). */
export function isChatFirstPath(pathname: string): boolean {
  return pathname === '/' || isProjectChatPath(pathname);
}

/** Extract the project slug from `/projects/<slug>…` or `/settings/projects/<slug>`. */
export function parseProjectSlugFromPathname(pathname: string): string | null {
  const fromProjects = PROJECT_ROUTE_SLUG_RE.exec(pathname);
  if (fromProjects?.[1]) return fromProjects[1];
  const fromSettings = PROJECT_SETTINGS_ROUTE_RE.exec(pathname);
  return fromSettings?.[1] ?? null;
}

/** A project's home surface — `/projects/<slug>`. */
export function projectHomeHref(slug: string): string {
  return `/projects/${slug}`;
}

export function projectSidebarHref(
  slug: string,
  segment: ProjectSidebarNavItem['segment'],
): string {
  if (segment === 'settings') {
    return `/settings/projects/${slug}`;
  }
  const base = `/projects/${slug}`;
  return segment === '' ? base : `${base}/${segment}`;
}

export function isProjectSidebarNavActive(
  pathname: string,
  slug: string,
  segment: ProjectSidebarNavItem['segment'],
): boolean {
  if (segment === 'settings') {
    return isProjectSettingsPath(pathname) && parseProjectSlugFromPathname(pathname) === slug;
  }
  const href = projectSidebarHref(slug, segment);
  if (segment === '') {
    return pathname === href;
  }
  return pathname === href || pathname.startsWith(`${href}/`);
}

export type ProjectsSidebarHeaderMeta = {
  title: string;
  back: { href: string; label: string };
};

/** True on `/projects*`, or project settings at `/settings/projects/[id]`. */
export function isProjectsSidebarPath(pathname: string): boolean {
  if (pathname.startsWith('/projects')) return true;
  return isProjectSettingsPath(pathname);
}

export function resolveProjectsSidebarHeader(
  pathname: string,
  activeProjectName?: string | null,
): ProjectsSidebarHeaderMeta | null {
  if (!isProjectsSidebarPath(pathname)) return null;

  const slug = parseProjectSlugFromPathname(pathname);
  if (slug) {
    const home = projectHomeHref(slug);
    const onProjectHome = pathname === home;
    return {
      back: onProjectHome
        ? { href: '/projects', label: 'Back to portfolio' }
        : { href: home, label: 'Back to project' },
      title: activeProjectName ?? 'Project',
    };
  }

  return {
    back: { href: '/', label: 'Back to workspace' },
    title: 'Portfolio',
  };
}
